import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from 'react';
import Toast from '../components/ui/Toast';
import { AnimatePresence } from 'framer-motion';

interface ToastItem {
  id: number;
  message: React.ReactNode;
  type: string;
}

interface ToastContextType {
  addToast: (
    message: React.ReactNode,
    type?: string,
    duration?: number
  ) => void;
  removeToast: (id: number) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

export const ToastProvider = ({ children }: { children: React.ReactNode }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timeoutsRef = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const addToast = (
    message: React.ReactNode,
    type: string = 'success',
    duration: number = 3000
  ) => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);

    const timeout = setTimeout(() => {
      removeToast(id);
    }, duration);

    timeoutsRef.current.set(id, timeout);
  };

  const removeToast = (id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));

    const timeout = timeoutsRef.current.get(id);
    if (timeout) {
      clearTimeout(timeout);
      timeoutsRef.current.delete(id);
    }
  };

  // Cleanup all timeouts when provider unmounts
  useEffect(() => {
    const timeouts = timeoutsRef.current;
    return () => {
      timeouts.forEach((t) => clearTimeout(t));
    };
  }, []);

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      {children}
      <div className="toast-container fixed top-[4%] right-0 m-4 z-[2000] space-y-2">
        <AnimatePresence>
          {toasts.map((toast) => (
            <Toast
              key={toast.id}
              message={toast.message}
              type={toast.type}
              onClose={() => removeToast(toast.id)}
            />
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const ctx = useContext(ToastContext);
  if (!ctx) return { addToast: () => {}, removeToast: () => {} };
  return ctx;
};
